<script setup>
import { ref, onMounted, watch } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
/* global axios */

const props = defineProps({
    contactId: { type: [Number, String], required: true },
});

const attributions = ref([]);
const isLoading = ref(false);
const expandedId = ref(null);
const accountId = useMapGetter('getCurrentAccountId');

async function fetchAttributions() {
    if (!props.contactId) return;
    isLoading.value = true;
    try {
        const url = `/api/v1/accounts/${accountId.value}/contacts/${props.contactId}/attributions`;
        const { data } = await axios.get(url);
        attributions.value = data.data || [];
    } catch {
        attributions.value = [];
    } finally {
        isLoading.value = false;
    }
}

function toggleExpand(id) {
    expandedId.value = expandedId.value === id ? null : id;
}

function formatDate(dateStr) {
    return new Date(dateStr).toLocaleString('pt-BR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
}

const SOURCE_CONFIG = {
    form_submission: {
        label: 'Formulário',
        icon: 'i-lucide-file-text',
        badgeClass: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    },
    meta_lead_ads: {
        label: 'Meta Lead Ads',
        icon: 'i-lucide-megaphone',
        badgeClass: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400',
    },
    tracking_trigger: {
        label: 'Tracking',
        icon: 'i-lucide-radar',
        badgeClass: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
    },
};

function getSourceConfig(source) {
    return SOURCE_CONFIG[source] || {
        label: source,
        icon: 'i-lucide-activity',
        badgeClass: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400',
    };
}

const UTM_LABELS = {
    utm_source: 'Fonte',
    utm_medium: 'Mídia',
    utm_campaign: 'Campanha',
    utm_term: 'Termo',
    utm_content: 'Conteúdo',
    campaign_id: 'Campaign ID',
    campaign_name: 'Campanha',
    ad_id: 'Ad ID',
    ad_name: 'Anúncio',
    form_name: 'Formulário',
    page_name: 'Página',
};

function getParamLabel(key) {
    return UTM_LABELS[key] || key;
}

function hasTrackingIds(attr) {
    return attr.gclid || attr.fbclid;
}

onMounted(fetchAttributions);
watch(() => props.contactId, fetchAttributions);
</script>

<template>
    <div>
        <div v-if="isLoading" class="flex justify-center py-3">
            <span class="animate-spin i-lucide-loader-2 size-4 text-n-slate-10" />
        </div>

        <div v-else-if="attributions.length === 0" class="py-3 text-center">
            <span class="i-lucide-route size-6 text-n-slate-8 mx-auto block mb-1" />
            <p class="text-xs text-n-slate-10">Nenhum registro de atribuição.</p>
        </div>

        <div v-else class="relative">
            <!-- Timeline line -->
            <div class="absolute left-[11px] top-3 bottom-3 w-px bg-n-weak" />

            <div v-for="(attr, idx) in attributions" :key="attr.id" class="relative flex gap-3 pb-3 last:pb-0">
                <!-- Timeline dot -->
                <div class="relative z-10 flex-shrink-0 mt-1.5">
                    <div class="size-[22px] rounded-full border-2 border-n-weak bg-n-solid-2 flex items-center justify-center"
                        :class="{ 'border-blue-400 bg-blue-50 dark:bg-blue-900/30': idx === 0 }">
                        <span :class="getSourceConfig(attr.source).icon" class="size-3"
                            :style="idx === 0 ? 'color: #3b82f6' : ''" />
                    </div>
                </div>

                <!-- Content card -->
                <div class="flex-1 min-w-0 rounded-lg border border-n-weak bg-n-solid-1 overflow-hidden transition-all"
                    :class="{ 'ring-1 ring-n-brand/20': expandedId === attr.id }">
                    <button class="w-full flex items-center gap-2 px-2.5 py-2 text-left hover:bg-n-alpha-1 transition"
                        @click="toggleExpand(attr.id)">
                        <div class="flex-1 min-w-0">
                            <span class="text-xs font-medium text-n-slate-12 truncate block">
                                {{ attr.source_name || getSourceConfig(attr.source).label }}
                            </span>
                            <span class="text-[10px] text-n-slate-10">{{ formatDate(attr.created_at) }}</span>
                        </div>
                        <span class="px-1.5 py-0.5 rounded text-[9px] font-medium flex-shrink-0"
                            :class="getSourceConfig(attr.source).badgeClass">
                            {{ getSourceConfig(attr.source).label }}
                        </span>
                        <span class="transition-transform duration-200 i-lucide-chevron-down size-3 text-n-slate-10"
                            :class="{ 'rotate-180': expandedId === attr.id }" />
                    </button>

                    <div v-if="expandedId === attr.id" class="px-2.5 pb-2.5 pt-1 border-t border-n-weak">
                        <!-- Google/Facebook Click IDs -->
                        <div v-if="hasTrackingIds(attr)" class="flex flex-wrap gap-1.5 mb-2">
                            <span v-if="attr.gclid"
                                class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 text-[10px] font-medium">
                                <span class="i-lucide-mouse-pointer-click size-2.5" />
                                GCLID
                            </span>
                            <span v-if="attr.fbclid"
                                class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 text-[10px] font-medium">
                                <span class="i-lucide-mouse-pointer-click size-2.5" />
                                FBCLID
                            </span>
                        </div>

                        <!-- UTM Params -->
                        <div v-for="(value, key) in attr.params" :key="key"
                            class="flex gap-2 py-1 border-b border-n-weak/50 last:border-0">
                            <span
                                class="text-[10px] font-medium text-n-slate-10 min-w-[70px] capitalize">{{ getParamLabel(key) }}</span>
                            <span class="text-[10px] text-n-slate-12 break-all">{{ value || '—' }}</span>
                        </div>

                        <!-- Referrer URL -->
                        <div v-if="attr.referrer_url"
                            class="flex gap-2 py-1 mt-1 border-t border-n-weak/50">
                            <span class="text-[10px] font-medium text-n-slate-10 min-w-[70px]">Origem</span>
                            <span class="text-[10px] text-n-slate-12 break-all truncate">{{ attr.referrer_url }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
